import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Form,
  Grid,
  Icon,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Button, F0DatePicker, F0Select, F0SelectItemProps } from '@factorialco/f0-react'
import {
  DatePickerValue,
  Input,
  NumberInput,
  OneModal,
  RichTextEditor,
  Select,
} from '@factorialco/f0-react/dist/experimental'
import {
  AccessForSelectFragment,
  AtsContractTypesEnum,
  AtsCreateRequisitionDocument,
  AtsJobPostingSalaryFormatEnum,
  AtsJobPostingSalaryPeriodEnum,
  AtsJobRequisitionsListDocument,
  AtsNewJobRequisitionModalPageDocument,
  AtsRequisitionReasonsEnum,
  ContractTypeFragment,
  OnboardingEntitiesWorkScheduleTimeEnum,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import TextEditor from 'modules/core/components/TextEditor'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { JobCatalogRoleSelector } from 'modules/ats/components/job_catalog/JobCatalogRoleSelector'

import { HiringTeamSelector } from './HiringTeamSelector'
import { SalaryOptions } from './SalaryOptions'
import { NewJobRequisitionModalSkeleton } from './Skeleton'
import { useSalaryConditionsAutoFill } from './useSalaryConditionsAutoFill'

const createOptions = (options: Array<{ value: string }>) => (value: unknown) =>
  typeof value === 'string' && options.some((option) => option.value === value)

interface RequisitionFormValues {
  authorId: number
  companyId: number
  jobPostingTitle: string
  note: string
  reason: AtsRequisitionReasonsEnum
  workplaceId: string
  teamId?: string
  hiringManagerId: number
  recruiterId: number | null
  numberOfPositions: number | null
  startDate: string | null
  jobCatalogRoleUuid: string | null
  jobCatalogLevelUuid: string | null
  contractType: AtsContractTypesEnum | null
  scheduleType: OnboardingEntitiesWorkScheduleTimeEnum | null
  salaryPeriod: AtsJobPostingSalaryPeriodEnum | null
  salaryFormat: AtsJobPostingSalaryFormatEnum | null
  salaryFromAmountInCents: number | null
  salaryToAmountInCents: number | null
}

const NewJobRequisitionModalPage = () => {
  const { company, access } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [openReasonToolTip, toggleOpenReasonToolTip] = useToggle()
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()
  const { loading, data } = useQuery(AtsNewJobRequisitionModalPageDocument)

  const onClose = () => {
    track(trackingEvents.ats.requestJobOpeningCancel)
    navigate(navigation.ats.jobs.requisitions.fullPath())
  }

  useEffect(() => {
    track(trackingEvents.ats.requestJobOpeningStart)
  }, [])

  const [createRequisition] = useMutation(AtsCreateRequisitionDocument, {
    extractorFn: (data) => data.atsMutations.createAtsRequisition,
    refetchQueries: [AtsJobRequisitionsListDocument],
  })

  const [selectedRecruiterItem, setSelectedRecruiterItem] =
    useState<AccessForSelectFragment | null>(null)
  const [selectedHiringManagerItem, setSelectedHiringManagerItem] =
    useState<AccessForSelectFragment | null>(null)

  const teams = data?.teams.teams ?? []
  const locations = data?.locations.locations ?? []
  const contractTypes = (data?.ats.contractTypesConnection.nodes ?? []).filter(
    (type): type is ContractTypeFragment => Boolean(type?.label)
  )
  const legalEntities = data?.companies.legalEntitiesConnection.nodes ?? []
  const jobCatalogRoles = data?.jobCatalog.treeNodesConnection.nodes ?? []

  const convertToCents = (value: number | undefined | null) =>
    !!value && isFinite(value) ? Math.round(value * 100) : 0

  const form = useForm<RequisitionFormValues>({
    defaultValues: {
      jobPostingTitle: '',
      note: '',
      reason: 'new_position',
      workplaceId: '',
      teamId: undefined,
      hiringManagerId: 0,
      recruiterId: null,
      numberOfPositions: 1,
      startDate: null,
      jobCatalogRoleUuid: null,
      jobCatalogLevelUuid: null,
      contractType: null,
      scheduleType: null,
      salaryPeriod: 'annual',
      salaryFormat: null,
      salaryFromAmountInCents: null,
      salaryToAmountInCents: null,
      authorId: access.id,
      companyId: company.id,
    },
    validate(values) {
      const errors: FormErrors<RequisitionFormValues> = {}

      if (!values.jobPostingTitle) {
        errors.jobPostingTitle = i18n.t('common.validations.empty')
      }

      if (!values.hiringManagerId) {
        errors.hiringManagerId = i18n.t('common.validations.empty')
      }

      if (!values.recruiterId) {
        errors.recruiterId = i18n.t('common.validations.empty')
      }

      if (!values.workplaceId) {
        errors.workplaceId = i18n.t('common.validations.empty')
      }

      if (!values.note) {
        errors.note = i18n.t('common.validations.empty')
      }

      if (!values.numberOfPositions || values.numberOfPositions <= 0) {
        errors.numberOfPositions = i18n.t('ats.jobs.invalid_number_of_vacancies')
      }

      if (values.jobCatalogRoleUuid && !values.jobCatalogLevelUuid) {
        errors.jobCatalogLevelUuid = i18n.t('common.validations.empty')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await createRequisition({
        variables: {
          jobPostingTitle: values.jobPostingTitle,
          reason: values.reason,
          note: values.note,
          hiringManagerId: Number(values.hiringManagerId),
          recruiterId: Number(values.recruiterId),
          workplaceId: Number(values.workplaceId),
          teamId: values.teamId ? Number(values.teamId) : null,
          authorId: access.id,
          companyId: company.id,
          numberOfPositions: values.numberOfPositions,
          startDate: values.startDate,
          jobCatalogRoleUuid: values.jobCatalogRoleUuid,
          jobCatalogLevelUuid: values.jobCatalogLevelUuid,
          contractType: values.contractType,
          scheduleType: values.scheduleType,
          salaryPeriod: values.salaryPeriod,
          salaryFromAmountInCents: convertToCents(values.salaryFromAmountInCents),
          salaryToAmountInCents: convertToCents(values.salaryToAmountInCents),
        },
      })

      const handled = handleResult(result, (resultErrors) => {
        setErrors(resultErrors)
      })

      track(trackingEvents.ats.requestJobOpeningSubmit)
      setFlashMessage(handled.success)

      if (handled.success) {
        onClose()
      }

      return handled
    },
  })

  useSalaryConditionsAutoFill({
    jobCatalogLevelUuid: form.values.jobCatalogLevelUuid,
    workplaceId: form.values.workplaceId,
    onSalaryConditionsLoaded: (salaryValues) => {
      form.setFieldValue('salaryFormat', salaryValues.salaryFormat)
      if (salaryValues.salaryPeriod) {
        form.setFieldValue('salaryPeriod', salaryValues.salaryPeriod)
      }
      form.setFieldValue('salaryFromAmountInCents', salaryValues.salaryFromAmountInCents)
      form.setFieldValue('salaryToAmountInCents', salaryValues.salaryToAmountInCents)
    },
  })

  const contractTypeOptions = contractTypes.map(({ id, label, country }) => ({
    label,
    country,
    value: id,
  }))

  const contractTypeSelectOptions = useMemo(() => {
    const grouped = groupBy(contractTypeOptions, (item) => item.country ?? 'all')
    const options: F0SelectItemProps<string>[] = []

    Object.entries(grouped).forEach(([countryKey, items], index) => {
      if (index > 0) {
        options.push({ type: 'separator' })
      }
      options.push({
        value: `header_${countryKey}`,
        label: countryKey,
        disabled: true,
      })
      // Add contract types for this country
      items
        .filter((i) => i.label)
        .sort((x, y) => x.label.localeCompare(y.label))
        .forEach(({ value, label }) => {
          options.push({ value, label })
        })
    })

    return options
  }, [contractTypeOptions])

  const locationOptions = locations
    .filter((location) => location?.name)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
    .map((location) => ({
      label: location?.name ?? '',
      value: String(location?.id ?? ''),
    }))

  const teamOptions = teams
    .filter((team) => team?.name)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
    .map((team) => ({
      label: team?.name ?? '',
      value: String(team?.id ?? ''),
    }))

  const selectedLegalEntityCurrency = legalEntities[0]?.currency ?? ''

  const scheduleTypeOptions: Array<{
    label: string
    value: OnboardingEntitiesWorkScheduleTimeEnum
  }> = [
    {
      label: i18n.t('ats.job_schedule.full_time'),
      value: 'full_time',
    },
    {
      label: i18n.t('ats.job_schedule.part_time'),
      value: 'part_time',
    },
  ]

  const reasonOptions: Array<{ label: string; value: AtsRequisitionReasonsEnum }> = [
    {
      label: i18n.t('ats.requisitions_form.reason.options.new_position'),
      value: 'new_position',
    },
    {
      label: i18n.t('ats.requisitions_form.reason.options.backfill'),
      value: 'backfill',
    },
  ]

  const setFlashMessage = (success: boolean) => {
    const type = success ? 'correct' : 'wrong'
    const message = success
      ? i18n.t('ats.requisitions_form.success')
      : i18n.t('ats.requisitions_form.error')

    app.setFlash({
      type: type,
      message: message,
      time: 2000,
    })
  }

  const startDatePickerValue = useMemo(() => {
    if (!form.values.startDate) {
      return undefined
    }

    const parsedDate = stringToDate(form.values.startDate, 'backend')

    if (!parsedDate.isValid()) {
      return undefined
    }

    const normalizedDate = parsedDate.toDate()

    return {
      granularity: 'day' as const,
      value: {
        from: normalizedDate,
        to: normalizedDate,
      },
    }
  }, [form.values.startDate])

  const handleStartDateChange = (date: DatePickerValue | undefined) => {
    const selectedDate = date?.value?.from

    if (!selectedDate) {
      form.setFieldValue('startDate', null)
      return
    }

    form.setFieldValue('startDate', i18nDateToString(selectedDate, 'backend'))
  }

  const isRequisitionReason = createOptions(reasonOptions)
  const isScheduleType = createOptions(scheduleTypeOptions)
  const isContractType = createOptions(contractTypeOptions)

  const isLoading = loading || richTextEditorLoading

  return (
    <OneModal onClose={onClose} isOpen>
      <OneModal.Header title={i18n.t('ats.requisitions_form.title')} />
      <OneModal.Content withPadding>
        {isLoading ? (
          <NewJobRequisitionModalSkeleton />
        ) : (
          <Form padding={null}>
            <Box width="full" gap="s16">
              <Text size="200" weight="semibold">
                {i18n.t('ats.job_posting_settings.basic_information.basic_info_title')}
              </Text>
              <NumberInput
                label={i18n.t('ats.requisitions_form.number_of_vacancies')}
                locale="en-US"
                name="numberOfPositions"
                onChange={(value) =>
                  form.setFieldValue('numberOfPositions', value === null ? null : Number(value))
                }
                placeholder="1"
                size="md"
                value={form.values.numberOfPositions}
                error={form.errors.numberOfPositions}
              />
              <Input
                label={i18n.t('ats.requisitions_form.job_title')}
                name="jobTitle"
                placeholder={i18n.t('ats.requisitions_form.job_title')}
                size="md"
                onChange={(value) => form.setFieldValue('jobPostingTitle', value)}
                value={form.values.jobPostingTitle}
                error={form.errors.jobPostingTitle}
              />
              {jobCatalogRoles.length > 0 && (
                <JobCatalogRoleSelector
                  jobCatalogRoles={jobCatalogRoles}
                  selectedRoleUuid={form.values.jobCatalogRoleUuid ?? undefined}
                  selectedRoleLevelUuid={form.values.jobCatalogLevelUuid ?? undefined}
                  onLevelSelect={(roleUuid, roleLevelUuid) => {
                    form.setValues({
                      ...form.values,
                      jobCatalogRoleUuid: roleUuid ?? null,
                      jobCatalogLevelUuid: roleLevelUuid ?? null,
                    })
                  }}
                  levelError={form.errors.jobCatalogLevelUuid}
                />
              )}
              <Grid variant="fixed">
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <Select
                    label={i18n.t('ats.requisitions_form.workplace')}
                    name="workplace"
                    placeholder={i18n.t('common.select')}
                    onChange={(value: string) => form.setFieldValue('workplaceId', value ?? '')}
                    size="md"
                    value={form.values.workplaceId || undefined}
                    options={locationOptions}
                    searchBoxPlaceholder={i18n.t(
                      'ats.jobs.new_job_modal.workplace_search_placeholder'
                    )}
                    searchEmptyMessage={i18n.t('ats.jobs.new_job_modal.search_no_results_found')}
                    error={form.errors.workplaceId}
                  />
                </Box>
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <Select
                    label={i18n.t('ats.requisitions_form.team')}
                    name="team"
                    placeholder={i18n.t('common.select')}
                    onChange={(value: string) => form.setFieldValue('teamId', value ?? undefined)}
                    size="md"
                    value={form.values.teamId || undefined}
                    options={teamOptions}
                    searchBoxPlaceholder={i18n.t('ats.jobs.new_job_modal.team_search_placeholder')}
                    searchEmptyMessage={i18n.t('ats.jobs.new_job_modal.search_no_results_found')}
                    error={form.errors.teamId}
                  />
                </Box>
              </Grid>
              <Grid variant="fixed">
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <Box gap="s4">
                    <Box flexDirection="row" alignItems="center" gap="s4">
                      <Text size="200" weight="medium">
                        {i18n.t('ats.requisitions_form.reason.label')}
                      </Text>
                      <Tooltip.Alt
                        open={openReasonToolTip}
                        toggle={toggleOpenReasonToolTip}
                        label={i18n.t('ats.requisitions_form.reason.tooltip')}
                        attachment="bottom center"
                      >
                        <Icon.Small icon="question" encircled="grey800" />
                      </Tooltip.Alt>
                    </Box>
                    <Select
                      label={i18n.t('ats.requisitions_form.reason.label')}
                      hideLabel
                      name="reason"
                      placeholder={i18n.t('common.select')}
                      onChange={(value: string) => {
                        if (isRequisitionReason(value)) {
                          form.setFieldValue('reason', value)
                        }
                      }}
                      size="md"
                      value={form.values.reason}
                      options={reasonOptions}
                      error={form.errors.reason}
                    />
                  </Box>
                </Box>
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <F0DatePicker
                    label={i18n.t('ats.jobs.new_job_modal.start_date')}
                    onChange={handleStartDateChange}
                    size="md"
                    value={startDatePickerValue}
                    placeholder={i18n.t('ats.jobs.new_job_modal.start_date_placeholder')}
                  />
                </Box>
              </Grid>
              <Text size="200" weight="semibold">
                {i18n.t('ats.job_posting_settings.details.title')}
              </Text>

              <HiringTeamSelector
                hiringManagerId={form.values.hiringManagerId}
                recruiterId={form.values.recruiterId}
                hiringManagerError={form.errors.hiringManagerId}
                recruiterError={form.errors.recruiterId}
                selectedHiringManagerItem={selectedHiringManagerItem}
                selectedRecruiterItem={selectedRecruiterItem}
                onHiringManagerChange={(value, item) => {
                  form.setFieldValue('hiringManagerId', value)
                  setSelectedHiringManagerItem(item)
                }}
                onRecruiterChange={(value, item) => {
                  form.setFieldValue('recruiterId', value)
                  setSelectedRecruiterItem(item)
                }}
              />
              <Grid variant="fixed">
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <F0Select
                    label={i18n.t('ats.job_posting_settings.details.contract_type_label')}
                    placeholder={i18n.t('common.select_basic')}
                    name="contractType"
                    value={form.values.contractType || undefined}
                    onChange={(value: string) => {
                      if (isContractType(value)) {
                        form.setFieldValue('contractType', value)
                      }
                    }}
                    options={contractTypeSelectOptions}
                    showSearchBox
                    searchEmptyMessage={i18n.t('ats.jobs.new_job_modal.search_no_results_found')}
                    size="md"
                  />
                </Box>
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <Select
                    label={i18n.t('ats.job_posting_settings.details.schedule_type_label')}
                    name="scheduleType"
                    placeholder={i18n.t('common.select_basic')}
                    onChange={(value: string) => {
                      if (isScheduleType(value)) {
                        form.setFieldValue('scheduleType', value)
                      } else {
                        form.setFieldValue('scheduleType', null)
                      }
                    }}
                    size="md"
                    value={form.values.scheduleType ?? undefined}
                    options={scheduleTypeOptions}
                  />
                </Box>
              </Grid>
              <Text size="200" weight="semibold">
                {i18n.t('ats.jobs_requisitions.requisition_detail.salary_details')}
              </Text>
              <SalaryOptions
                salaryPeriod={form.values.salaryPeriod}
                salaryFormat={form.values.salaryFormat}
                salaryFromAmountInCents={form.values.salaryFromAmountInCents}
                salaryToAmountInCents={form.values.salaryToAmountInCents}
                currency={selectedLegalEntityCurrency}
                onSalaryPeriodChange={(value) => form.setFieldValue('salaryPeriod', value)}
                onSalaryFormatChange={(value) => form.setFieldValue('salaryFormat', value)}
                onSalaryFromChange={(value) => form.setFieldValue('salaryFromAmountInCents', value)}
                onSalaryToChange={(value) => form.setFieldValue('salaryToAmountInCents', value)}
              />
              <Box gap="s8">
                <Text size="200" weight="medium">
                  {i18n.t('ats.requisitions_form.note')}
                </Text>
                {canUseRichTextEditor ? (
                  <RichTextEditor
                    {...editorConfig}
                    title={i18n.t('ats.requisitions_form.note')}
                    onChange={(editorData) => form.setFieldValue('note', editorData?.value ?? '')}
                    placeholder={i18n.t('ats.requisitions_form.note')}
                    initialEditorState={{
                      content: form.values.note,
                    }}
                    maxCharacters={4000}
                  />
                ) : (
                  <TextEditor
                    onChange={(noteValue) => form.setFieldValue('note', noteValue)}
                    placeholder={i18n.t('ats.requisitions_form.note')}
                    initialEditorState={{
                      data: form.values.note,
                    }}
                    height="s600"
                    enableBetaFeatures={false}
                  />
                )}
              </Box>
            </Box>
          </Form>
        )}
      </OneModal.Content>
      <OneModal.Footer>
        <Box flexDirection="row" justifyContent="flexEnd" gap="s12" width="full">
          <F0Button onClick={onClose} label={i18n.t('common.cancel')} variant="outline" />
          <F0Button
            onClick={() => form.handleSubmit()}
            label={i18n.t('ats.requisitions_form.send_request')}
            loading={form.isSubmitting}
            disabled={!form.isValid}
          />
        </Box>
      </OneModal.Footer>
    </OneModal>
  )
}

export default NewJobRequisitionModalPage
